import styled from '@emotion/styled';
import type { WarningVariant } from '../types';

/** Maps variant to palette tokens for background, border, and accent (icon/label/line/chevron). */
export function getVariantColors(
  theme: {
    palette: {
      bg: Record<string, string>;
      border: Record<string, string>;
      fg: Record<string, string>;
      utility: {
        error: { 50: string; [key: number]: string | undefined };
        marineBlue: { 50: string; 600: string; [key: number]: string | undefined };
      };
    };
  },
  variant: WarningVariant
): { bg: string; border: string; accent: string } {
  switch (variant) {
    case 'red':
      return {
        bg: theme.palette.utility.error[50], // #FBE8E9 (Figma: utility-error-50)
        border: theme.palette.border.errorSolid, // error[500] #DA1E28 (Figma: border-error-solid)
        accent: theme.palette.fg.errorPrimary, // error[500]
      };
    case 'blue':
      return {
        bg: theme.palette.utility.marineBlue[50], // #EEF0FF (Figma: utility-marineblue-50)
        border: theme.palette.utility.marineBlue[600], // #4154D4 (Figma: border-accent-primary)
        accent: theme.palette.fg.infoPrimary, // marineBlue[500]
      };
    case 'yellow':
    default:
      return {
        bg: theme.palette.bg.warningSubtle,
        border: theme.palette.border.warning,
        accent: theme.palette.fg.warningPrimary,
      };
  }
}

type VariantProps = { $variant?: WarningVariant };

export const WarningBlock = styled.div<VariantProps>`
  padding: ${p => p.theme.s.md};
  border-radius: ${p => p.theme.s.xs};
  background-color: ${p => getVariantColors(p.theme, p.$variant ?? 'yellow').bg};
  border: 1px solid ${p => getVariantColors(p.theme, p.$variant ?? 'yellow').border};
  margin-bottom: ${p => p.theme.s.xs};
`;

export const WarningHeading = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${p => p.theme.s.xs};
  flex: 1;
  min-width: 0;
  font-size: ${p => p.theme.typography?.bodySm?.fontSize ?? '0.875rem'};
  line-height: ${p => p.theme.typography?.bodySm?.lineHeight ?? '1.25rem'};

  & svg {
    flex-shrink: 0;
  }
  & > span {
    flex: 1;
    min-width: 0;
    font-weight: 500;
    color: ${p => p.theme.palette.text.primary};
  }
`;

export const WarningHeadingRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${p => p.theme.s.sm};
  width: 100%;
`;

export const WarningDividerRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.s.sm};
  width: 100%;
  margin-top: ${p => p.theme.s.xs};
`;

export const WarningDividerLabel = styled.span<VariantProps>`
  font-size: ${p => p.theme.typography?.bodyXs?.fontSize ?? '0.75rem'};
  font-weight: 500;
  line-height: ${p => p.theme.typography?.bodyXs?.lineHeight ?? '1.125rem'};
  color: ${p => getVariantColors(p.theme, p.$variant ?? 'yellow').accent};
  flex-shrink: 0;
`;

export const WarningDividerLine = styled.div<VariantProps>`
  flex: 1;
  min-width: 0;
  border-top: 1px solid ${p => getVariantColors(p.theme, p.$variant ?? 'yellow').accent};
`;

export const WarningChevronButton = styled.button<VariantProps>`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${p => getVariantColors(p.theme, p.$variant ?? 'yellow').accent};
  display: flex;
  align-items: center;
  flex-shrink: 0;
  & svg,
  & svg path {
    color: inherit;
    fill: currentColor;
  }
`;

export const WarningListBox = styled.div`
  padding: ${p => p.theme.s.xxxs} 0 ${p => p.theme.s.xxxs} 0;
  margin-top: ${p => p.theme.s.xxxs};

  & > .MuiList-root .MuiListItem-root {
    padding: ${p => p.theme.s.xxxs} 0;
  }
`;

export const ItemLine = styled.div`
  font-size: ${p => p.theme.typography?.bodyXs?.fontSize ?? '0.75rem'};
  line-height: ${p => p.theme.typography?.bodyXs?.lineHeight ?? '1.125rem'};

  & .removed-item-name {
    color: ${p => p.theme.palette.text.primary};
    font-weight: 500;
  }
  & .removed-item-id {
    color: ${p => p.theme.palette.text.secondary};
  }
`;

export const ShowAllButton = styled.button`
  background: none;
  border: none;
  padding: ${p => p.theme.s.sm} 0 ${p => p.theme.s.xxxs} 0;
  cursor: pointer;
  color: ${p => p.theme.palette.text.secondary};
  font-size: ${p => p.theme.typography?.bodyXs?.fontSize ?? '0.75rem'};
  line-height: ${p => p.theme.typography?.bodyXs?.lineHeight ?? '1.125rem'};
  text-align: left;
  display: block;

  &:hover {
    text-decoration: underline;
  }
`;
